import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { promiseWithResolvers } from 'vscode/vscode/vs/base/common/async';
import { Disposable, DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { assertIsDefined } from 'vscode/vscode/vs/base/common/types';
import { localizeWithPath, localize2WithPath } from 'vscode/vscode/vs/nls';
import { registerAction2, Action2, MenuId, MenuItemAction, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IMenuService } from 'vscode/vscode/vs/platform/actions/common/actions.service';
import { ICommandService } from 'vscode/vscode/vs/platform/commands/common/commands.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';

var NewFileTemplatesManager_1;
const _moduleId = "vs/workbench/contrib/welcomeViews/common/newFile.contribution";
const builtInSource = ( localizeWithPath(_moduleId, 0, "Built-In"));
const category = ( localize2WithPath(_moduleId, 1, 'Create'));
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'welcome.showNewFileEntries',
            title: ( localize2WithPath(_moduleId, 2, 'New File...')),
            category,
            f1: true,
            keybinding: {
                primary: 512  + 2048  + 256  + 44 ,
                weight: 200 ,
            },
            menu: {
                id: MenuId.MenubarFileMenu,
                group: '1_new',
                order: 2
            }
        });
    }
    async run(accessor) {
        return assertIsDefined(NewFileTemplatesManager.Instance).run();
    }
});
let NewFileTemplatesManager = class NewFileTemplatesManager extends Disposable {
    static { NewFileTemplatesManager_1 = this; }
    constructor(quickInputService, contextKeyService, commandService, keybindingService, menuService) {
        super();
        this.quickInputService = quickInputService;
        this.contextKeyService = contextKeyService;
        this.commandService = commandService;
        this.keybindingService = keybindingService;
        NewFileTemplatesManager_1.Instance = this;
        this._register({ dispose() { if (NewFileTemplatesManager_1.Instance === this) {
                NewFileTemplatesManager_1.Instance = undefined;
            } } });
        this.menu = menuService.createMenu(MenuId.NewFile, contextKeyService);
    }
    allEntries() {
        const items = [];
        for (const [groupName, group] of this.menu.getActions({ renderShortTitle: true })) {
            for (const action of group) {
                if (action instanceof MenuItemAction) {
                    items.push({ commandID: action.item.id, from: action.item.source?.title ?? builtInSource, title: action.label, group: groupName });
                }
            }
        }
        return items;
    }
    async run() {
        const entries = this.allEntries();
        if (entries.length === 0) {
            throw Error('Unexpected empty new items list');
        }
        else if (entries.length === 1) {
            this.commandService.executeCommand(entries[0].commandID);
            return true;
        }
        else {
            return this.selectNewEntry(entries);
        }
    }
    async selectNewEntry(entries) {
        const { promise: resultPromise, resolve: resolveResult } = promiseWithResolvers();
        const disposables = ( (new DisposableStore()));
        const qp = this.quickInputService.createQuickPick();
        qp.title = ( localizeWithPath(_moduleId, 3, "New File..."));
        qp.placeholder = ( localizeWithPath(_moduleId, 4, "Select File Type or Enter File Name..."));
        qp.sortByLabel = false;
        qp.matchOnDetail = true;
        qp.matchOnDescription = true;
        const sortCategories = (a, b) => {
            const categoryPriority = { 'file': 1, 'notebook': 2 };
            if (categoryPriority[a.group] && categoryPriority[b.group]) {
                if (categoryPriority[a.group] !== categoryPriority[b.group]) {
                    return categoryPriority[b.group] - categoryPriority[a.group];
                }
            }
            else if (categoryPriority[a.group]) {
                return 1;
            }
            else if (categoryPriority[b.group]) {
                return -1;
            }
            if (a.from === builtInSource) {
                return 1;
            }
            if (b.from === builtInSource) {
                return -1;
            }
            return a.from.localeCompare(b.from);
        };
        const displayCategory = {
            'file': ( localizeWithPath(_moduleId, 5, "File")),
            'notebook': ( localizeWithPath(_moduleId, 6, "Notebook")),
        };
        const refreshQp = (entries) => {
            const items = [];
            let lastSeparator;
            entries
                .sort((a, b) => -sortCategories(a, b))
                .forEach((entry) => {
                const command = entry.commandID;
                const keybinding = this.keybindingService.lookupKeybinding(command || '', this.contextKeyService);
                if (lastSeparator !== entry.group) {
                    items.push({
                        type: 'separator',
                        label: displayCategory[entry.group] ?? entry.group
                    });
                    lastSeparator = entry.group;
                }
                items.push({
                    ...entry,
                    label: entry.title,
                    type: 'item',
                    keybinding,
                    buttons: command ? [
                        {
                            iconClass: 'codicon codicon-gear',
                            tooltip: ( localizeWithPath(_moduleId, 7, "Configure Keybinding"))
                        }
                    ] : [],
                    detail: '',
                    description: entry.from,
                });
            });
            qp.items = items;
        };
        refreshQp(entries);
        disposables.add(this.menu.onDidChange(() => refreshQp(this.allEntries())));
        disposables.add(qp.onDidChangeValue((val) => {
            if (val === '') {
                refreshQp(entries);
                return;
            }
            const currentTextEntry = {
                commandID: 'workbench.action.files.newFile',
                commandArgs: { languageId: undefined, viewType: undefined, fileName: val },
                title: ( localizeWithPath(_moduleId, 8, "Create New File ({0})", val)),
                group: 'file',
                from: builtInSource,
            };
            refreshQp([currentTextEntry, ...entries]);
        }));
        disposables.add(qp.onDidAccept(async (e) => {
            const selected = qp.selectedItems[0];
            resolveResult(!!selected);
            qp.hide();
            if (selected) {
                await this.commandService.executeCommand(selected.commandID, selected.commandArgs);
            }
        }));
        disposables.add(qp.onDidHide(() => {
            qp.dispose();
            disposables.dispose();
            resolveResult(false);
        }));
        disposables.add(qp.onDidTriggerItemButton(e => {
            qp.hide();
            this.commandService.executeCommand('workbench.action.openGlobalKeybindings', e.item.commandID);
            resolveResult(false);
        }));
        qp.show();
        return resultPromise;
    }
};
NewFileTemplatesManager = NewFileTemplatesManager_1 = ( (__decorate([
    ( (__param(0, IQuickInputService))),
    ( (__param(1, IContextKeyService))),
    ( (__param(2, ICommandService))),
    ( (__param(3, IKeybindingService))),
    ( (__param(4, IMenuService)))
], NewFileTemplatesManager)));
( (Registry.as(Extensions.Workbench)))
    .registerWorkbenchContribution(NewFileTemplatesManager, 3 );
MenuRegistry.appendMenuItem(MenuId.NewFile, {
    group: 'file',
    command: {
        id: 'workbench.action.files.newUntitledFile',
        title: ( localizeWithPath(_moduleId, 9, "Text File"))
    },
    order: 1
});
